import { useId } from 'react';
import { LiksSlider } from '../LiksSlider/LiksSlider';
import { LiksToggleGroup } from '../LiksToggleGroup/LiksToggleGroup';
import {
  clampRagMaxResults,
  clampRagMinScore,
  formatRagMinScore,
  isRagRetrievalPresetId,
  matchRagRetrievalPreset,
  RAG_MAX_RESULTS_MAX,
  RAG_MAX_RESULTS_MIN,
  RAG_MAX_RESULTS_STEP,
  RAG_MIN_SCORE_MAX,
  RAG_MIN_SCORE_MIN,
  RAG_MIN_SCORE_STEP,
  RAG_RETRIEVAL_PRESET_IDS,
  RAG_RETRIEVAL_PRESETS,
  type RagRetrievalPresetId,
} from '../../../utils/ragRetrievalSettings';
import './LiksRagRetrievalSettings.css';

export type LiksRagRetrievalSettingsProps = {
  maxResults: number;
  minScore: number;
  onMaxResultsChange: (value: number) => void;
  onMinScoreChange: (value: number) => void;
  disabled?: boolean;
  presetsName: string;
  presetsLegend: string;
  presetLabels: Record<RagRetrievalPresetId, string>;
  maxResultsLabel: string;
  maxResultsDescription: string;
  minScoreLabel: string;
  minScoreDescription: string;
  className?: string;
};

export function LiksRagRetrievalSettings({
  maxResults,
  minScore,
  onMaxResultsChange,
  onMinScoreChange,
  disabled = false,
  presetsName,
  presetsLegend,
  presetLabels,
  maxResultsLabel,
  maxResultsDescription,
  minScoreLabel,
  minScoreDescription,
  className,
}: LiksRagRetrievalSettingsProps) {
  const maxResultsId = useId();
  const minScoreId = useId();
  const selectedPreset = matchRagRetrievalPreset(maxResults, minScore);

  const handlePresetChange = (value: string) => {
    if (!isRagRetrievalPresetId(value)) {
      return;
    }
    const preset = RAG_RETRIEVAL_PRESETS[value];
    onMaxResultsChange(preset.maxResults);
    onMinScoreChange(preset.minScore);
  };

  return (
    <div
      className={['liks-rag-retrieval-settings', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="liks-rag-retrieval-settings__field">
        <p className="liks-rag-retrieval-settings__label">{presetsLegend}</p>
        <LiksToggleGroup
          name={presetsName}
          legend={presetsLegend}
          value={selectedPreset}
          disabled={disabled}
          options={RAG_RETRIEVAL_PRESET_IDS.map((id) => ({
            value: id,
            label: presetLabels[id],
          }))}
          onChange={handlePresetChange}
          className="liks-rag-retrieval-settings__presets"
        />
      </div>

      <div className="liks-rag-retrieval-settings__field">
        <label
          className="liks-rag-retrieval-settings__label"
          htmlFor={maxResultsId}
        >
          {maxResultsLabel}
        </label>
        <p className="liks-rag-retrieval-settings__description">
          {maxResultsDescription}
        </p>
        <LiksSlider
          id={maxResultsId}
          value={maxResults}
          min={RAG_MAX_RESULTS_MIN}
          max={RAG_MAX_RESULTS_MAX}
          step={RAG_MAX_RESULTS_STEP}
          disabled={disabled}
          aria-label={maxResultsLabel}
          onChange={(value) => onMaxResultsChange(clampRagMaxResults(value))}
        />
      </div>

      <div className="liks-rag-retrieval-settings__field">
        <label
          className="liks-rag-retrieval-settings__label"
          htmlFor={minScoreId}
        >
          {minScoreLabel}
        </label>
        <p className="liks-rag-retrieval-settings__description">
          {minScoreDescription}
        </p>
        <LiksSlider
          id={minScoreId}
          value={minScore}
          min={RAG_MIN_SCORE_MIN}
          max={RAG_MIN_SCORE_MAX}
          step={RAG_MIN_SCORE_STEP}
          disabled={disabled}
          valueLabel={formatRagMinScore(minScore)}
          aria-label={minScoreLabel}
          onChange={(value) => onMinScoreChange(clampRagMinScore(value))}
        />
      </div>
    </div>
  );
}
